import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Wrench, Trash2, Camera, ImageIcon, Undo2, FileDown } from "lucide-react";
import { api, API, authedFetch, formatApiErrorDetail } from "../lib/api";
import { compressImage } from "../lib/imageCompression";
import { uploadWithRetry } from "../lib/uploadWithRetry";
import { formatDate } from "./dpi";

/**
 * "Sistema Danni" flow: shows every damage entry from the latest revision
 * that has not been marked as repaired yet, with a per-row "Sistemato" action
 * that uploads a mandatory photo and creates a repair record on the backend.
 *
 * Repairs are also listed at the bottom of the dialog so the operator can
 * undo an accidental one (Annulla ripristino).
 */

const LVL_COLOR = {
  verde: "#10B981",
  arancione: "#FFCC00",
  rosso: "#FF3B30",
};

const LVL_TEXT_COLOR = {
  verde: "#FFFFFF",
  arancione: "#000000",
  rosso: "#FFFFFF",
};

const NOTE1_LBL = {
  tabella_portata_errata: "Tabella portata errata",
  tabella_portata_mancante: "Tabella portata mancante",
  deformazione: "Deformazione",
  modifica_non_conforme: "Modifica non conforme",
  torsione: "Torsione",
  fissaggio_mancante: "Fissaggio mancante",
  pezzo_mancante: "Pezzo mancante",
  antisgancio_mancante: "Antisgancio mancante",
  basi_non_conformi: "Basi non conformi",
  carico_pericoloso: "Carico pericoloso",
};

// Client-side signature mirror of the backend _damage_entry_signature.
// Must stay in sync with /app/backend/server.py to filter already-repaired
// entries out of the "sistema danni" list.
function normalizeCount(v) {
  if (Array.isArray(v)) return [...v].map(Number).sort((a, b) => a - b);
  return v;
}
async function sha1Hex(str) {
  const buf = new TextEncoder().encode(str);
  const hash = await crypto.subtle.digest("SHA-1", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
export async function damageEntrySignature(entry) {
  const payload = {
    area: (entry?.area || "").trim() || "Generale",
    riferimento_scaffalatura: (entry?.riferimento_scaffalatura || "").trim(),
    posto: (entry?.posto || "").trim(),
    montante_anteriore: entry?.montante_anteriore ?? null,
    montante_posteriore: entry?.montante_posteriore ?? null,
    diagonali: entry?.diagonali ?? null,
    diagonali_count: normalizeCount(entry?.diagonali_count) ?? null,
    traversino_basso: entry?.traversino_basso ?? null,
    traversino_basso_count: normalizeCount(entry?.traversino_basso_count) ?? null,
    corrente: entry?.corrente ?? null,
    corrente_count: normalizeCount(entry?.corrente_count) ?? null,
    nota1: entry?.nota1 ?? null,
    nota2: (entry?.nota2 || "").trim(),
  };
  // Sort keys to match Python json.dumps(sort_keys=True)
  const keys = Object.keys(payload).sort();
  const canonical = "{" + keys.map((k) => JSON.stringify(k) + ": " + JSON.stringify(payload[k])).join(", ") + "}";
  const hash = await sha1Hex(canonical);
  return hash.slice(0, 16);
}

function damageDescription(e) {
  const bits = [];
  const map = [
    ["montante_anteriore", "Mont. Ant."],
    ["montante_posteriore", "Mont. Post."],
    ["diagonali", "Diag."],
    ["traversino_basso", "Trav."],
    ["corrente", "Corr."],
  ];
  for (const [k, lbl] of map) {
    const v = e?.[k];
    if (!v) continue;
    let extra = "";
    if (k === "diagonali" || k === "traversino_basso" || k === "corrente") {
      const cnt = e[`${k}_count`];
      if (Array.isArray(cnt) && cnt.length) {
        extra = ` #${[...cnt].sort((a, b) => a - b).join(",")}`;
      } else if (cnt) {
        extra = ` #${cnt}`;
      }
    }
    bits.push({ label: lbl + extra, color: LVL_COLOR[v], text: LVL_TEXT_COLOR[v] });
  }
  return bits;
}

export default function RepairDamagesDialog({
  open,
  scaffold,
  latestRevision,
  onClose,
  onChanged,
}) {
  const [repairs, setRepairs] = useState([]);
  const [entrySigs, setEntrySigs] = useState({}); // entryKey -> signature
  const [loading, setLoading] = useState(true);
  const [pendingEntryKey, setPendingEntryKey] = useState(null); // which entry is being uploaded
  const [uploadingKey, setUploadingKey] = useState(null);
  const [error, setError] = useState("");
  const [downloadingReport, setDownloadingReport] = useState(false);
  const fileInputRef = useRef(null);

  const scaffoldId = scaffold?.id;
  const revisionId = latestRevision?.id;
  const entries = useMemo(
    () => latestRevision?.damage_entries || [],
    [latestRevision],
  );

  // Compute signatures for all entries on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const map = {};
      for (let i = 0; i < entries.length; i++) {
        const sig = await damageEntrySignature(entries[i]);
        map[`${i}`] = sig;
      }
      if (!cancelled) setEntrySigs(map);
    })();
    return () => { cancelled = true; };
  }, [open, entries]);

  const loadRepairs = async () => {
    if (!scaffoldId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/scaffolds/${scaffoldId}/repairs`);
      setRepairs(data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      setError("");
      loadRepairs();
    }
  }, [open, scaffoldId]);

  const repairedSigs = useMemo(() => {
    return new Set((repairs || []).map((r) => r.entry_signature));
  }, [repairs]);

  const pendingEntries = useMemo(() => {
    return entries
      .map((e, i) => ({ e, i, sig: entrySigs[`${i}`] }))
      .filter((row) => row.sig && !repairedSigs.has(row.sig));
  }, [entries, entrySigs, repairedSigs]);

  const openFilePicker = (entryKey) => {
    setPendingEntryKey(entryKey);
    // small timeout to ensure ref is set
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !pendingEntryKey) return;
    const key = pendingEntryKey;
    setPendingEntryKey(null);
    setUploadingKey(key);
    setError("");
    try {
      const compressed = await compressImage(file);
      const data = await uploadWithRetry(compressed);
      const entry = entries[Number(key)];
      const sig = entrySigs[key];
      await api.post(`/scaffolds/${scaffoldId}/repairs`, {
        entry_signature: sig,
        entry_snapshot: entry,
        source_revision_id: revisionId,
        photo: data.path,
      });
      await loadRepairs();
      onChanged?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Errore nel salvataggio");
    }
    setUploadingKey(null);
  };

  const undoRepair = async (repairId) => {
    if (!window.confirm("Annullare questo ripristino?")) return;
    try {
      await api.delete(`/scaffolds/${scaffoldId}/repairs/${repairId}`);
      await loadRepairs();
      onChanged?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const downloadReport = async () => {
    if (!scaffoldId) return;
    setDownloadingReport(true);
    try {
      const res = await authedFetch(`${API}/scaffolds/${scaffoldId}/repairs/pdf`);
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        alert(`Errore nella generazione del PDF (HTTP ${res.status}). ${txt.slice(0, 200)}`);
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      let filename = `ripristini_${(scaffold?.reference || "scaffalatura").replace(/\s+/g, "_")}.pdf`;
      const m = cd.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      alert(`Errore di rete: ${err.message || err}`);
    }
    setDownloadingReport(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      data-testid="repair-damages-dialog"
      onClick={onClose}
    >
      <div
        className="bru-card w-full max-w-4xl p-6 mt-8 mb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="tag-label">SISTEMA DANNI</div>
            <h3 className="text-2xl font-bold flex items-center gap-2">
              <Wrench className="w-6 h-6" />
              Registra interventi di ripristino
            </h3>
            <p className="text-xs text-neutral-500 mt-1">
              I danni sistemati non compariranno nella prossima verifica se
              scegli &quot;Usa verifica precedente&quot;. Alla fine puoi
              scaricare il <b>Report Ripristini</b> con foto e date.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="bru-btn bru-btn-ghost !px-2 !py-1"
            data-testid="close-repair-dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 border border-[#ff3b30] text-[#ff3b30] text-sm" data-testid="repair-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="tag-label py-8 text-center">CARICAMENTO…</div>
        ) : (
          <>
            {/* Danni da sistemare */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="tag-label">DA SISTEMARE ({pendingEntries.length})</div>
                {repairs.length > 0 && (
                  <button
                    type="button"
                    onClick={downloadReport}
                    disabled={downloadingReport}
                    className="bru-btn bru-btn-secondary !px-3 !py-1.5 !text-xs"
                    data-testid="download-repair-report"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    {downloadingReport ? "…" : `Report ripristini (${repairs.length})`}
                  </button>
                )}
              </div>
              {pendingEntries.length === 0 ? (
                <div className="p-6 border-[1.5px] border-dashed border-neutral-300 text-center text-sm text-neutral-500">
                  Nessun danno da sistemare. Tutti i danni dell&apos;ultima
                  verifica risultano ripristinati.
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingEntries.map(({ e, i }) => (
                    <div
                      key={`p-${i}`}
                      className="border-[1.5px] border-neutral-200 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                      data-testid={`repair-entry-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="bru-badge bg-white">#{e.number}</span>
                          <span className="bru-badge bg-[#f5f5f5]">{e.area || "Generale"}</span>
                          {e.riferimento_scaffalatura && (
                            <span className="text-xs font-bold">Rif: {e.riferimento_scaffalatura}</span>
                          )}
                          {e.posto && (
                            <span className="text-xs">Posto: <b>{e.posto}</b></span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mb-1">
                          {damageDescription(e).map((b, k) => (
                            <span
                              key={k}
                              className="text-[11px] font-bold px-2 py-0.5"
                              style={{ backgroundColor: b.color, color: b.text }}
                            >
                              {b.label}
                            </span>
                          ))}
                        </div>
                        {(e.nota1 || e.nota2) && (
                          <div className="text-xs text-neutral-600">
                            {e.nota1 && <span>{NOTE1_LBL[e.nota1] || e.nota1}</span>}
                            {e.nota1 && e.nota2 && <span> · </span>}
                            {e.nota2 && <span>{e.nota2}</span>}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => openFilePicker(String(i))}
                        disabled={uploadingKey === String(i)}
                        className="bru-btn bru-btn-primary !py-2 !px-4 shrink-0"
                        data-testid={`mark-repaired-${i}`}
                      >
                        <Camera className="w-4 h-4" />
                        {uploadingKey === String(i) ? "Caricamento…" : "Sistemato"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Già sistemati */}
            {repairs.length > 0 && (
              <div>
                <div className="tag-label mb-3">SISTEMATI ({repairs.length})</div>
                <div className="space-y-2">
                  {repairs.map((r) => {
                    const e = r.entry_snapshot || {};
                    return (
                      <div
                        key={r.id}
                        className="border-[1.5px] border-neutral-200 bg-[#F5FCF7] p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                        data-testid={`repaired-entry-${r.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="bru-badge bg-[#10B981] text-white">RIPRISTINATO</span>
                            <span className="bru-badge bg-white">{formatDate(r.repair_date)}</span>
                            <span className="bru-badge bg-[#f5f5f5]">{e.area || "Generale"}</span>
                            {e.riferimento_scaffalatura && (
                              <span className="text-xs font-bold">Rif: {e.riferimento_scaffalatura}</span>
                            )}
                            {e.posto && (
                              <span className="text-xs">Posto: <b>{e.posto}</b></span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {damageDescription(e).map((b, k) => (
                              <span
                                key={k}
                                className="text-[11px] font-bold px-2 py-0.5"
                                style={{ backgroundColor: b.color, color: b.text }}
                              >
                                {b.label}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {r.photo && (
                            <a
                              href={`${API}/files/${r.photo}`}
                              target="_blank"
                              rel="noreferrer"
                              className="bru-btn bru-btn-secondary !py-1.5 !px-3 !text-xs"
                              data-testid={`view-repair-photo-${r.id}`}
                            >
                              <ImageIcon className="w-3.5 h-3.5" />
                              Foto
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => undoRepair(r.id)}
                            className="bru-btn bru-btn-secondary !text-[#ff3b30] !py-1.5 !px-3 !text-xs"
                            data-testid={`undo-repair-${r.id}`}
                          >
                            <Undo2 className="w-3.5 h-3.5" />
                            Annulla
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onFilePicked}
          data-testid="repair-photo-input"
        />
      </div>
    </div>
  );
}
